import {
  Chapter,
  type ChapterImageQualities,
  type TitleChapters,
} from "~/src/api";
import { natSort } from "~/utils/functions";
import {
  ImagePageGroup,
  InterludePageGroup,
  type PageGroupTypes,
  PlaceholderPageGroup,
  SubscribeLockedPageGroup,
} from "~/src/reader/managers/pageGroup";
import type { UnwrapNestedRefs } from "vue";
import { ImagePageLoader } from "~/src/reader/managers/cdnImage";

export function createChapterPages(
  pages: Awaited<ReturnType<typeof Chapter.getPageUrls>>["source"],
  quality?: ChapterImageQualities,
) {
  return pages.map((page, index) => {
    const [width, height] = page.resolution.split("x").map(Number);
    return new ImagePageLoader({ src: page.imageUrl, width, height, quality });
  });
}

// TODO: get offset amount working
export function groupPages(
  chapterId: string,
  pageLoaders: ImagePageLoader[],
  pagesPerGroup = 1,
  offsetAmount = 0,
) {
  const pageGroups: ImagePageGroup[] = [];

  let i = 0;
  while (i < pageLoaders.length) {
    const wouldBePageGroup = pageLoaders.slice(i, i + pagesPerGroup);

    const isFirstPage = i === 0;
    const isLastPage = i === pageLoaders.length - 1;
    const aPageIsLandscape = wouldBePageGroup.some(
      (page) => page.width > page.height,
    );

    const totalPagesInGroup =
      isFirstPage || isLastPage || aPageIsLandscape ? 1 : pagesPerGroup;

    const pagesToGroup = pageLoaders.slice(i, i + totalPagesInGroup);
    const group = new ImagePageGroup(chapterId, pagesToGroup);
    group.id = `${chapterId}-${i}-${i + totalPagesInGroup - 1}`;
    pageGroups.push(group);

    i += totalPagesInGroup;
  }

  return pageGroups;
}

export const natSortNulls = (a: string | null, b: string | null) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, undefined, { numeric: true });
};

export const locateChapterInDirection = (
  currId: string,
  aggregate: TitleChapters["attributes"]["list"],
  direction = 1,
): string | null => {
  const currGroupVol = aggregate.find((vol) =>
    vol.chapters.some((ch) => ch.id === currId),
  );
  assertDefined(currGroupVol);

  const currGroupCh = currGroupVol.chapters.find((ch) => ch.id === currId);
  assertDefined(currGroupCh);

  let oneshot = false;
  if (!currGroupVol.volume && !currGroupCh.chapter) oneshot = true;

  const currentVol = oneshot ? aggregate[0] : currGroupVol;
  assertDefined(currentVol);

  const currChapters = currentVol.chapters
    .filter(
      ({ translatedLanguage }) =>
        translatedLanguage === currGroupCh.translatedLanguage,
    )
    .sort((a, b) => natSort(a.chapter, b.chapter));

  const currIndex = currChapters.findIndex((ch) => ch.id === currId);
  const nextCh = currChapters[currIndex + direction];

  if (!nextCh) {
    const currVolIndex = aggregate.findIndex(
      ({ volume }) => volume === currentVol.volume,
    );
    if (currVolIndex < 0)
      throw new Error("Could not find the current volume index");
    const nextVol = aggregate[currVolIndex + direction];
    if (nextVol === undefined) {
      const oneshot = aggregate[aggregate.length - 1]?.chapters
        .filter(
          ({ translatedLanguage }) =>
            translatedLanguage === currGroupCh.translatedLanguage,
        )
        .find((x) => !x.chapter);
      if (oneshot && currGroupVol.volume && direction < 0) return oneshot.id;

      return null;
    }

    const nextChapters = nextVol.chapters
      .filter(
        ({ translatedLanguage }) =>
          translatedLanguage === currGroupCh.translatedLanguage,
      )
      .sort((a, b) => natSort(a.chapter, b.chapter));
    if (!nextChapters)
      throw new Error("Something went wrong and the chapter was not found");
    if (direction < 0) return nextChapters[nextChapters.length - 1].id;
    return nextChapters.filter(({ chapter }) => !!chapter).at(0)?.id ?? null;
  }

  if (!currentVol.volume && !nextCh.chapter && direction < 0) {
    const prevVol = aggregate[aggregate.length - 2];

    if (!prevVol) {
      throw new Error(
        "Could not find the previous volume of the current chapter.",
      );
    }

    const nextChapters = prevVol.chapters.filter(
      ({ chapter, translatedLanguage }) =>
        !!chapter && translatedLanguage === currGroupCh.translatedLanguage,
    );
    return nextChapters[nextChapters.length - 1].id;
  }

  return nextCh.id;
};

export const isImagePageGroup = (
  page: UnwrapNestedRefs<PageGroupTypes>,
): page is ImagePageGroup => !!(page as ImagePageGroup).pages;
export const isInterludePageGroup = (
  page: UnwrapNestedRefs<PageGroupTypes>,
): page is InterludePageGroup => !!(page as InterludePageGroup).type;
export const isPlaceholderPageGroup = (
  page: UnwrapNestedRefs<PageGroupTypes>,
): page is PlaceholderPageGroup =>
  (page as PlaceholderPageGroup).width !== undefined &&
  !(page as ImagePageGroup).pages &&
  (page as PlaceholderPageGroup).id === "placeholder";
export const isSubscribePageGroup = (
  page: UnwrapNestedRefs<PageGroupTypes>,
): page is SubscribeLockedPageGroup =>
  !(page as InterludePageGroup).type &&
  !(page as ImagePageGroup).pages &&
  !isPlaceholderPageGroup(page);

export const isContentPageGroup = (page: UnwrapNestedRefs<PageGroupTypes>) =>
  isImagePageGroup(page) ||
  isSubscribePageGroup(page) ||
  isPlaceholderPageGroup(page);
